import { jalaliMonthDates, jalaliPayMonthOf, toJalaliString } from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, formatMoney, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  type BalanceSheetReport,
  type CashFlowReport,
  type IncomeStatementReport,
  getBalanceSheetReport,
  getCashFlowReport,
  getIncomeStatementReport,
  getStatementExportUrl,
} from "../../api.js";

type StatementKind = "balance-sheet" | "income-statement" | "cash-flow";

export function StatementsTab() {
  const [statementKind, setStatementKind] = useState<StatementKind>("balance-sheet");
  const [level, setLevel] = useState<"general" | "subsidiary" | "group">("general");
  const [comparePrior, setComparePrior] = useState<boolean>(true);

  // Initialize with current Jalali year and month
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const initialJalali = useMemo(() => {
    try {
      return jalaliPayMonthOf(todayIso);
    } catch {
      return { year: 1405, month: 8 };
    }
  }, [todayIso]);

  const [jYear, setJYear] = useState<number>(initialJalali.year);
  const [periodPreset, setPeriodPreset] = useState<string>("full-year");

  // Derive fromDate and toDate based on preset
  const { fromDate, toDate, asOfDate } = useMemo(() => {
    const getDates = (y: number, m: number): { start: string; end: string } => {
      const days = jalaliMonthDates(y, m);
      const start = days[0] ?? "";
      const end = days[days.length - 1] ?? "";
      return { start, end };
    };

    try {
      if (periodPreset === "q1") {
        const d1 = getDates(jYear, 1).start;
        const d2 = getDates(jYear, 3).end;
        return { fromDate: d1, toDate: d2, asOfDate: d2 };
      }
      if (periodPreset === "q2") {
        const d1 = getDates(jYear, 4).start;
        const d2 = getDates(jYear, 6).end;
        return { fromDate: d1, toDate: d2, asOfDate: d2 };
      }
      if (periodPreset === "q3") {
        const d1 = getDates(jYear, 7).start;
        const d2 = getDates(jYear, 9).end;
        return { fromDate: d1, toDate: d2, asOfDate: d2 };
      }
      if (periodPreset === "q4") {
        const d1 = getDates(jYear, 10).start;
        const d2 = getDates(jYear, 12).end;
        return { fromDate: d1, toDate: d2, asOfDate: d2 };
      }
      if (periodPreset.startsWith("m-")) {
        const m = parseInt(periodPreset.replace("m-", ""), 10);
        const { start, end } = getDates(jYear, m);
        return { fromDate: start, toDate: end, asOfDate: end };
      }
      // full-year
      const d1 = getDates(jYear, 1).start;
      const d2 = getDates(jYear, 12).end;
      return { fromDate: d1, toDate: d2, asOfDate: d2 };
    } catch {
      return { fromDate: "2026-03-21", toDate: "2027-03-20", asOfDate: todayIso };
    }
  }, [jYear, periodPreset, todayIso]);

  const [customAsOf, setCustomAsOf] = useState<string>("");
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");

  const activeAsOfDate = customAsOf.trim() ? customAsOf.trim() : asOfDate;
  const activeFromDate = customFrom.trim() ? customFrom.trim() : fromDate;
  const activeToDate = customTo.trim() ? customTo.trim() : toDate;

  // 1. Balance Sheet Query
  const balanceSheetQuery = useQuery<BalanceSheetReport>({
    queryKey: ["accounting", "statements", "balance-sheet", activeAsOfDate, level],
    queryFn: () =>
      getBalanceSheetReport({
        asOfDate: activeAsOfDate,
        level,
      }),
    enabled: statementKind === "balance-sheet",
  });

  // 2. Income Statement Query
  const incomeStatementQuery = useQuery<IncomeStatementReport>({
    queryKey: ["accounting", "statements", "income-statement", activeFromDate, activeToDate, comparePrior],
    queryFn: () =>
      getIncomeStatementReport({
        fromDate: activeFromDate,
        toDate: activeToDate,
        comparePriorPeriod: comparePrior,
      }),
    enabled: statementKind === "income-statement",
  });

  // 3. Cash Flow Query
  const cashFlowQuery = useQuery<CashFlowReport>({
    queryKey: ["accounting", "statements", "cash-flow", activeFromDate, activeToDate],
    queryFn: () =>
      getCashFlowReport({
        fromDate: activeFromDate,
        toDate: activeToDate,
      }),
    enabled: statementKind === "cash-flow",
  });

  const exportUrl = useMemo(() => {
    if (statementKind === "balance-sheet") {
      return getStatementExportUrl("balance-sheet", {
        asOfDate: activeAsOfDate,
        level,
      });
    }
    if (statementKind === "income-statement") {
      return getStatementExportUrl("income-statement", {
        fromDate: activeFromDate,
        toDate: activeToDate,
        comparePriorPeriod: comparePrior,
      });
    }
    return getStatementExportUrl("cash-flow", {
      fromDate: activeFromDate,
      toDate: activeToDate,
    });
  }, [statementKind, activeAsOfDate, activeFromDate, activeToDate, level, comparePrior]);

  const bs = balanceSheetQuery.data;
  const is = incomeStatementQuery.data;
  const cf = cashFlowQuery.data;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <style>{`
        @media print {
          .no-print, header.top, nav.nav, aside.rail, .top-bar, .statement-nav, .statement-filters {
            display: none !important;
          }
          body, #root, .app-shell, .main {
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .a4-sheet {
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 5mm !important;
          }
        }
        .a4-sheet {
          background: #fff;
          color: #111;
          border-radius: var(--r-card);
          border: 1px solid var(--line);
          padding: var(--s6);
          box-shadow: var(--sh-card);
        }
        .a4-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid var(--line);
          padding-bottom: var(--s4);
          margin-bottom: var(--s5);
        }
        .statement-table {
          width: 100%;
          border-collapse: collapse;
          font-size: var(--fs-14);
        }
        .statement-table th, .statement-table td {
          border: 1px solid var(--line);
          padding: var(--s2) var(--s3);
        }
        .statement-table th {
          background: var(--bg-hover);
          font-weight: 600;
        }
        .subtotal-row {
          background: var(--bg-muted);
          font-weight: bold;
        }
        .total-row {
          background: var(--bg-surface);
          font-weight: bold;
          font-size: var(--fs-15);
        }
      `}</style>

      {/* Top Navigation & Statement Switcher */}
      <div className="row wrap justify-between items-center no-print" style={{ gap: "var(--s3)" }}>
        <div id="statement-selector">
          <Seg<StatementKind>
            label="انتخاب صورت مالی"
            value={statementKind}
            onChange={setStatementKind}
            options={[
              { value: "balance-sheet", label: "ترازنامه (وضعیت مالی)" },
              { value: "income-statement", label: "صورت سود و زیان (عملکرد)" },
              { value: "cash-flow", label: "خلاصه جریان وجوه نقد" },
            ]}
          />
        </div>

        <div className="row items-center" style={{ gap: "var(--s2)" }}>
          <a
            id="btn-export-statement-csv"
            href={exportUrl}
            download
            className="btn btn-outline btn-sm row items-center"
            style={{ textDecoration: "none" }}
          >
            دانلود اکسل (CSV)
          </a>
          <Button
            id="btn-print-statement"
            variant="primary"
            onClick={() => {
              window.print();
            }}
          >
            چاپ A4
          </Button>
        </div>
      </div>

      {/* Period & Filter Controls */}
      <Card className="no-print">
        <div
          className="row wrap items-center justify-between"
          style={{ gap: "var(--s3)", padding: "var(--s3)" }}
        >
          <div className="row wrap items-center" style={{ gap: "var(--s3)" }}>
            <label className="row items-center" style={{ gap: "var(--s2)" }}>
              <span className="text-sm font-semibold">سال مالی:</span>
              <select
                id="select-fiscal-year"
                className="input input-sm"
                value={jYear}
                onChange={(e) => {
                  setJYear(Number(e.target.value));
                }}
              >
                <option value={1403}>۱۴۰۳</option>
                <option value={1404}>۱۴۰۴</option>
                <option value={1405}>۱۴۰۵</option>
                <option value={1406}>۱۴۰۶</option>
              </select>
            </label>

            <label className="row items-center" style={{ gap: "var(--s2)" }}>
              <span className="text-sm font-semibold">دوره گزارش:</span>
              <select
                id="select-period-preset"
                className="input input-sm"
                value={periodPreset}
                onChange={(e) => {
                  setPeriodPreset(e.target.value);
                  setCustomFrom("");
                  setCustomTo("");
                  setCustomAsOf("");
                }}
              >
                <option value="full-year">کل سال مالی</option>
                <option value="q1">۳ ماهه اول (بهار)</option>
                <option value="q2">۳ ماهه دوم (تابستان)</option>
                <option value="q3">۳ ماهه سوم (پاییز)</option>
                <option value="q4">۳ ماهه چهارم (زمستان)</option>
                <option value="m-1">فروردین</option>
                <option value="m-2">اردیبهشت</option>
                <option value="m-3">خرداد</option>
                <option value="m-4">تیر</option>
                <option value="m-5">مرداد</option>
                <option value="m-6">شهریور</option>
                <option value="m-7">مهر</option>
                <option value="m-8">آبان</option>
                <option value="m-9">آذر</option>
                <option value="m-10">دی</option>
                <option value="m-11">بهمن</option>
                <option value="m-12">اسفند</option>
              </select>
            </label>

            {statementKind === "balance-sheet" && (
              <label className="row items-center" style={{ gap: "var(--s2)" }}>
                <span className="text-sm font-semibold">سطح حساب:</span>
                <select
                  id="select-bs-level"
                  className="input input-sm"
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value as "general" | "subsidiary" | "group");
                  }}
                >
                  <option value="general">سطح کل</option>
                  <option value="subsidiary">سطح معین</option>
                  <option value="group">سطح گروه</option>
                </select>
              </label>
            )}

            {statementKind === "income-statement" && (
              <label className="row items-center" style={{ gap: "var(--s2)", cursor: "pointer" }}>
                <input
                  id="checkbox-compare-prior"
                  type="checkbox"
                  checked={comparePrior}
                  onChange={(e) => {
                    setComparePrior(e.target.checked);
                  }}
                />
                <span className="text-sm">مقایسه با دوره مشابه سال قبل</span>
              </label>
            )}
          </div>

          <div className="row items-center text-sm text-muted" style={{ gap: "var(--s2)" }}>
            <span>بازه میلادی فعال:</span>
            <code className="text-xs">
              {statementKind === "balance-sheet"
                ? `تا ${activeAsOfDate}`
                : `${activeFromDate} تا ${activeToDate}`}
            </code>
          </div>
        </div>
      </Card>

      {/* Main Printable A4 Sheet */}
      <div className="a4-sheet" dir="rtl">
        <div className="a4-header">
          <div>
            <h1 style={{ margin: 0, fontSize: "var(--fs-20)", fontWeight: "bold" }}>
              مطبخ و تالار شهر — سیستم حسابداری مالی
            </h1>
            <p style={{ margin: "var(--s1) 0 0", color: "var(--muted)", fontSize: "var(--fs-14)" }}>
              {statementKind === "balance-sheet" &&
                `ترازنامه مالی به تاریخ ${toJalaliString(activeAsOfDate)} (${activeAsOfDate})`}
              {statementKind === "income-statement" &&
                `صورت سود و زیان دوره از ${toJalaliString(activeFromDate)} تا ${toJalaliString(activeToDate)}`}
              {statementKind === "cash-flow" &&
                `خلاصه گردش و جریان وجوه نقد از ${toJalaliString(activeFromDate)} تا ${toJalaliString(activeToDate)}`}
            </p>
          </div>
          <div style={{ textAlign: "left" }}>
            <span style={{ fontSize: "var(--fs-13)", color: "var(--muted)" }}>
              سال مالی: {faDigits(jYear)}
            </span>
          </div>
        </div>

        {/* ---------------------------------------------------- */}
        {/* 1. BALANCE SHEET VIEW */}
        {/* ---------------------------------------------------- */}
        {statementKind === "balance-sheet" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {balanceSheetQuery.isLoading && (
              <div className="p-4 text-center">در حال بارگذاری ترازنامه...</div>
            )}
            {bs && (
              <>
                <div
                  className="row items-center justify-between"
                  style={{
                    padding: "var(--s3)",
                    background: bs.isBalanced ? "var(--c-success-soft)" : "var(--c-danger-soft)",
                    borderRadius: "var(--r-btn)",
                  }}
                >
                  <div className="row items-center" style={{ gap: "var(--s2)" }}>
                    <span id="balance-sheet-status-chip">
                      <Chip tone={bs.isBalanced ? "ok" : "coral"}>
                        {bs.isBalanced ? "تراز متعادل است" : "عدم تعادل در ترازنامه"}
                      </Chip>
                    </span>
                    <span className="text-sm font-semibold" id="balance-sheet-balanced">
                      {bs.isBalanced
                        ? "جمع دارایی‌ها دقیقاً با جمع بدهی‌ها و حقوق صاحبان سهام مطابقت دارد."
                        : `مغایرت به میزان ${formatMoney(Math.abs(bs.differenceRial))} ریال`}
                    </span>
                  </div>
                  <div className="text-sm font-bold">
                    جمع کل دارایی‌ها: {formatMoney(bs.assets.totalAssetsRial)} ریال
                  </div>
                </div>

                <div
                  className="row wrap"
                  style={{ gap: "var(--s4)", alignItems: "flex-start", width: "100%" }}
                >
                  {/* Right Side: Assets */}
                  <div style={{ flex: "1 1 45%", minWidth: "300px" }}>
                    <h3
                      style={{
                        margin: "0 0 var(--s2)",
                        fontSize: "var(--fs-16)",
                        fontWeight: "bold",
                        borderBottom: "2px solid var(--line)",
                        paddingBottom: "var(--s1)",
                      }}
                    >
                      دارایی‌ها (Assets)
                    </h3>
                    <table className="statement-table">
                      <thead>
                        <tr>
                          <th>کد</th>
                          <th>شرح حساب</th>
                          <th style={{ textAlign: "left" }}>مبلغ (ریال)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="subtotal-row">
                          <td colSpan={3}>۱۱. دارایی‌های جاری</td>
                        </tr>
                        {bs.assets.currentAssets.items.map((it) => (
                          <tr key={it.code}>
                            <td style={{ width: "60px" }}>{faDigits(it.code)}</td>
                            <td>{it.name}</td>
                            <td style={{ textAlign: "left" }}>
                              {it.balanceRial < 0
                                ? `(${formatMoney(Math.abs(it.balanceRial))})`
                                : formatMoney(it.balanceRial)}
                            </td>
                          </tr>
                        ))}
                        <tr className="subtotal-row">
                          <td colSpan={2}>جمع دارایی‌های جاری</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(bs.assets.currentAssets.subtotalRial)}
                          </td>
                        </tr>

                        <tr className="subtotal-row">
                          <td colSpan={3}>۱۲. دارایی‌های غیرجاری</td>
                        </tr>
                        {bs.assets.nonCurrentAssets.items.map((it) => (
                          <tr key={it.code}>
                            <td style={{ width: "60px" }}>{faDigits(it.code)}</td>
                            <td>{it.name}</td>
                            <td style={{ textAlign: "left" }}>
                              {it.balanceRial < 0
                                ? `(${formatMoney(Math.abs(it.balanceRial))})`
                                : formatMoney(it.balanceRial)}
                            </td>
                          </tr>
                        ))}
                        <tr className="subtotal-row">
                          <td colSpan={2}>جمع دارایی‌های غیرجاری</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(bs.assets.nonCurrentAssets.subtotalRial)}
                          </td>
                        </tr>

                        <tr className="total-row" id="row-total-assets">
                          <td colSpan={2}>جمع کل دارایی‌ها</td>
                          <td style={{ textAlign: "left" }} id="total-assets-amount">
                            {formatMoney(bs.assets.totalAssetsRial)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Left Side: Liabilities & Equity */}
                  <div style={{ flex: "1 1 45%", minWidth: "300px" }}>
                    <h3
                      style={{
                        margin: "0 0 var(--s2)",
                        fontSize: "var(--fs-16)",
                        fontWeight: "bold",
                        borderBottom: "2px solid var(--line)",
                        paddingBottom: "var(--s1)",
                      }}
                    >
                      بدهی‌ها و حقوق صاحبان سهام (Liabilities & Equity)
                    </h3>
                    <table className="statement-table">
                      <thead>
                        <tr>
                          <th>کد</th>
                          <th>شرح حساب</th>
                          <th style={{ textAlign: "left" }}>مبلغ (ریال)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="subtotal-row">
                          <td colSpan={3}>۲۱. بدهی‌های جاری</td>
                        </tr>
                        {bs.liabilities.currentLiabilities.items.map((it) => (
                          <tr key={it.code}>
                            <td style={{ width: "60px" }}>{faDigits(it.code)}</td>
                            <td>{it.name}</td>
                            <td style={{ textAlign: "left" }}>{formatMoney(it.balanceRial)}</td>
                          </tr>
                        ))}
                        <tr className="subtotal-row">
                          <td colSpan={2}>جمع بدهی‌های جاری</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(bs.liabilities.currentLiabilities.subtotalRial)}
                          </td>
                        </tr>

                        {bs.liabilities.nonCurrentLiabilities.items.length > 0 && (
                          <>
                            <tr className="subtotal-row">
                              <td colSpan={3}>۲۲. بدهی‌های غیرجاری</td>
                            </tr>
                            {bs.liabilities.nonCurrentLiabilities.items.map((it) => (
                              <tr key={it.code}>
                                <td style={{ width: "60px" }}>{faDigits(it.code)}</td>
                                <td>{it.name}</td>
                                <td style={{ textAlign: "left" }}>{formatMoney(it.balanceRial)}</td>
                              </tr>
                            ))}
                            <tr className="subtotal-row">
                              <td colSpan={2}>جمع بدهی‌های غیرجاری</td>
                              <td style={{ textAlign: "left" }}>
                                {formatMoney(bs.liabilities.nonCurrentLiabilities.subtotalRial)}
                              </td>
                            </tr>
                          </>
                        )}

                        <tr className="subtotal-row" style={{ background: "var(--bg-hover)" }}>
                          <td colSpan={2}>جمع کل بدهی‌ها</td>
                          <td style={{ textAlign: "left" }} id="total-liabilities-amount">
                            {formatMoney(bs.liabilities.totalLiabilitiesRial)}
                          </td>
                        </tr>

                        <tr className="subtotal-row">
                          <td colSpan={3}>۳. حقوق صاحبان سهام و سرمایه</td>
                        </tr>
                        {bs.equity.capitalAndReserves.items.map((it) => (
                          <tr key={it.code}>
                            <td style={{ width: "60px" }}>{faDigits(it.code)}</td>
                            <td>{it.name}</td>
                            <td style={{ textAlign: "left" }}>
                              {it.balanceRial < 0
                                ? `(${formatMoney(Math.abs(it.balanceRial))})`
                                : formatMoney(it.balanceRial)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td>-</td>
                          <td className="font-semibold text-primary">سود (زیان) خالص دوره مالی جاری</td>
                          <td
                            style={{ textAlign: "left" }}
                            className="font-semibold"
                            id="bs-current-period-profit"
                          >
                            {bs.equity.currentPeriodProfitLossRial < 0
                              ? `(${formatMoney(Math.abs(bs.equity.currentPeriodProfitLossRial))})`
                              : formatMoney(bs.equity.currentPeriodProfitLossRial)}
                          </td>
                        </tr>
                        <tr className="subtotal-row">
                          <td colSpan={2}>جمع کل حقوق صاحبان سهام</td>
                          <td style={{ textAlign: "left" }} id="total-equity-amount">
                            {formatMoney(bs.equity.totalEquityRial)}
                          </td>
                        </tr>

                        <tr className="total-row" id="row-total-liab-equity">
                          <td colSpan={2}>جمع بدهی‌ها و حقوق صاحبان سهام</td>
                          <td style={{ textAlign: "left" }} id="total-liabilities-and-equity-amount">
                            {formatMoney(bs.totalLiabilitiesAndEquityRial)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* 2. INCOME STATEMENT VIEW */}
        {/* ---------------------------------------------------- */}
        {statementKind === "income-statement" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {incomeStatementQuery.isLoading && (
              <div className="p-4 text-center">در حال بارگذاری صورت سود و زیان...</div>
            )}
            {is && (
              <>
                {/* Summary KPI Cards */}
                <div
                  className="row wrap no-print"
                  style={{ gap: "var(--s3)", justifyContent: "space-between" }}
                >
                  <div style={{ flex: "1 1 200px" }}>
                    <Card>
                      <div style={{ padding: "var(--s3)" }}>
                        <div className="text-xs text-muted">فروش خالص</div>
                        <div className="text-lg font-bold" id="stat-net-revenue">
                          {formatMoney(is.netRevenueRial)} ریال
                        </div>
                      </div>
                    </Card>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <Card>
                      <div style={{ padding: "var(--s3)" }}>
                        <div className="text-xs text-muted">سود ناخالص</div>
                        <div className="text-lg font-bold text-success" id="stat-gross-profit">
                          {formatMoney(is.grossProfitRial)} ریال
                        </div>
                      </div>
                    </Card>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <Card>
                      <div style={{ padding: "var(--s3)" }}>
                        <div className="text-xs text-muted">هزینه‌های عملیاتی</div>
                        <div className="text-lg font-bold text-danger" id="stat-operating-expenses">
                          {formatMoney(is.totalOperatingExpensesRial)} ریال
                        </div>
                      </div>
                    </Card>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <Card>
                      <div
                        style={{
                          padding: "var(--s3)",
                          background:
                            is.netProfitLossRial >= 0 ? "var(--c-success-soft)" : "var(--c-danger-soft)",
                        }}
                      >
                        <div className="text-xs text-muted">سود (زیان) خالص</div>
                        <div
                          className="text-lg font-bold"
                          id="stat-net-profit"
                          style={{
                            color: is.netProfitLossRial >= 0 ? "var(--c-success)" : "var(--c-danger)",
                          }}
                        >
                          {formatMoney(is.netProfitLossRial)} ریال
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Primary Financial Statement Table */}
                <table className="statement-table">
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>کد</th>
                      <th>شرح حساب / قلم مالی</th>
                      <th style={{ textAlign: "left" }}>دوره جاری (ریال)</th>
                      {is.hasComparative && (
                        <>
                          <th style={{ textAlign: "left" }}>دوره قبل (ریال)</th>
                          <th style={{ textAlign: "left" }}>تغییر (ریال)</th>
                          <th style={{ textAlign: "center", width: "90px" }}>درصد تغییر</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>۴۱</td>
                      <td>درآمدهای عملیاتی فروش غذا</td>
                      <td style={{ textAlign: "left" }}>{formatMoney(is.grossRevenueRial)}</td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>{formatMoney(is.priorGrossRevenueRial ?? 0)}</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(is.grossRevenueRial - (is.priorGrossRevenueRial ?? 0))}
                          </td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>۴۲</td>
                      <td>کسر می‌شود: برگشت از فروش و تخفیفات اعطایی</td>
                      <td style={{ textAlign: "left" }}>({formatMoney(is.salesReturnsDiscountsRial)})</td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>
                            ({formatMoney(is.priorSalesReturnsDiscountsRial ?? 0)})
                          </td>
                          <td style={{ textAlign: "left" }}>-</td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>
                    <tr className="subtotal-row">
                      <td>-</td>
                      <td>فروش خالص</td>
                      <td style={{ textAlign: "left" }} id="pnl-net-revenue">
                        {formatMoney(is.netRevenueRial)}
                      </td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>{formatMoney(is.priorNetRevenueRial ?? 0)}</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(is.netRevenueRial - (is.priorNetRevenueRial ?? 0))}
                          </td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>

                    <tr>
                      <td>۵</td>
                      <td>کسر می‌شود: بهای تمام‌شده کالای فروش‌رفته</td>
                      <td style={{ textAlign: "left" }} id="pnl-cogs">
                        ({formatMoney(is.cogsRial)})
                      </td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>({formatMoney(is.priorCogsRial ?? 0)})</td>
                          <td style={{ textAlign: "left" }}>-</td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>

                    <tr className="subtotal-row" style={{ background: "var(--bg-hover)" }}>
                      <td>-</td>
                      <td>سود (زیان) ناخالص</td>
                      <td style={{ textAlign: "left" }} id="pnl-gross-profit">
                        {formatMoney(is.grossProfitRial)}
                      </td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>{formatMoney(is.priorGrossProfitRial ?? 0)}</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(is.grossProfitRial - (is.priorGrossProfitRial ?? 0))}
                          </td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>

                    <tr>
                      <td>۶۱</td>
                      <td>هزینه‌های توزیع، حمل و فروش</td>
                      <td style={{ textAlign: "left" }}>({formatMoney(is.distributionExpensesRial)})</td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>
                            ({formatMoney(is.priorDistributionExpensesRial ?? 0)})
                          </td>
                          <td style={{ textAlign: "left" }}>-</td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>۶۲</td>
                      <td>هزینه‌های اداری، عمومی و مدیریت</td>
                      <td style={{ textAlign: "left" }}>({formatMoney(is.administrativeExpensesRial)})</td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>
                            ({formatMoney(is.priorAdministrativeExpensesRial ?? 0)})
                          </td>
                          <td style={{ textAlign: "left" }}>-</td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>

                    <tr className="subtotal-row">
                      <td>-</td>
                      <td>جمع هزینه‌های عملیاتی</td>
                      <td style={{ textAlign: "left" }} id="pnl-total-operating-expenses">
                        ({formatMoney(is.totalOperatingExpensesRial)})
                      </td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>
                            ({formatMoney(is.priorTotalOperatingExpensesRial ?? 0)})
                          </td>
                          <td style={{ textAlign: "left" }}>-</td>
                          <td style={{ textAlign: "center" }}>-</td>
                        </>
                      )}
                    </tr>

                    <tr
                      className="total-row"
                      style={{
                        background:
                          is.netProfitLossRial >= 0 ? "var(--c-success-soft)" : "var(--c-danger-soft)",
                      }}
                      id="pnl-net-profit-row"
                    >
                      <td>-</td>
                      <td className="font-bold">سود (زیان) خالص دوره مالی</td>
                      <td style={{ textAlign: "left" }} className="font-bold" id="pnl-net-profit">
                        {is.netProfitLossRial < 0
                          ? `(${formatMoney(Math.abs(is.netProfitLossRial))})`
                          : formatMoney(is.netProfitLossRial)}
                      </td>
                      {is.hasComparative && (
                        <>
                          <td style={{ textAlign: "left" }}>{formatMoney(is.priorNetProfitLossRial ?? 0)}</td>
                          <td style={{ textAlign: "left" }}>
                            {formatMoney(is.netProfitLossRial - (is.priorNetProfitLossRial ?? 0))}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {is.priorNetProfitLossRial
                              ? `${faDigits(
                                  Math.round(
                                    ((is.netProfitLossRial - is.priorNetProfitLossRial) /
                                      Math.abs(is.priorNetProfitLossRial)) *
                                      100,
                                  ),
                                )}٪`
                              : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* 3. CASH FLOW SUMMARY VIEW */}
        {/* ---------------------------------------------------- */}
        {statementKind === "cash-flow" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {cashFlowQuery.isLoading && (
              <div className="p-4 text-center">در حال بارگذاری جریان وجوه نقد...</div>
            )}
            {cf && (
              <>
                <div
                  className="row wrap justify-between items-center"
                  style={{
                    padding: "var(--s3)",
                    background: "var(--bg-muted)",
                    borderRadius: "var(--r-btn)",
                  }}
                >
                  <div className="row items-center" style={{ gap: "var(--s3)" }}>
                    <span className="text-sm font-semibold">مانده اول دوره وجوه نقد و بانک:</span>
                    <span className="text-base font-bold" id="cash-flow-opening">
                      {formatMoney(cf.openingCashAndBankRial)} ریال
                    </span>
                  </div>
                  <div className="row items-center" style={{ gap: "var(--s3)" }}>
                    <span className="text-sm font-semibold">مانده پایان دوره وجوه نقد و بانک:</span>
                    <span className="text-base font-bold text-primary" id="cash-flow-closing">
                      {formatMoney(cf.closingCashAndBankRial)} ریال
                    </span>
                  </div>
                </div>

                {/* Inflows Section */}
                <div>
                  <h4
                    style={{
                      margin: "0 0 var(--s2)",
                      color: "var(--c-success)",
                      fontWeight: "bold",
                    }}
                  >
                    ۱. جریان‌های نقدی ورودی (دریافت‌های دوره)
                  </h4>
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th style={{ width: "80px" }}>کد طرف</th>
                        <th>شرح حساب طرف مقابل</th>
                        <th>دسته‌بندی جریان</th>
                        <th style={{ textAlign: "left" }}>مبلغ دریافتی (ریال)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cf.inflows.map((inf) => (
                        <tr key={inf.counterAccountCode}>
                          <td>{faDigits(inf.counterAccountCode)}</td>
                          <td>{inf.counterAccountName}</td>
                          <td>
                            <Chip tone="ok">{inf.category}</Chip>
                          </td>
                          <td style={{ textAlign: "left" }}>{formatMoney(inf.amountRial)}</td>
                        </tr>
                      ))}
                      {cf.inflows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            هیچ ورودی نقدی در این بازه ثبت نشده است.
                          </td>
                        </tr>
                      )}
                      <tr className="subtotal-row">
                        <td colSpan={3}>جمع کل جریان‌های ورودی</td>
                        <td style={{ textAlign: "left" }} id="cash-flow-total-inflow">
                          {formatMoney(cf.totalInflowRial)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Outflows Section */}
                <div>
                  <h4
                    style={{
                      margin: "0 0 var(--s2)",
                      color: "var(--c-danger)",
                      fontWeight: "bold",
                    }}
                  >
                    ۲. جریان‌های نقدی خروجی (پرداخت‌های دوره)
                  </h4>
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th style={{ width: "80px" }}>کد طرف</th>
                        <th>شرح حساب طرف مقابل</th>
                        <th>دسته‌بندی جریان</th>
                        <th style={{ textAlign: "left" }}>مبلغ پرداختی (ریال)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cf.outflows.map((outf) => (
                        <tr key={outf.counterAccountCode}>
                          <td>{faDigits(outf.counterAccountCode)}</td>
                          <td>{outf.counterAccountName}</td>
                          <td>
                            <Chip tone="coral">{outf.category}</Chip>
                          </td>
                          <td style={{ textAlign: "left" }}>{formatMoney(outf.amountRial)}</td>
                        </tr>
                      ))}
                      {cf.outflows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            هیچ خروجی نقدی در این بازه ثبت نشده است.
                          </td>
                        </tr>
                      )}
                      <tr className="subtotal-row">
                        <td colSpan={3}>جمع کل جریان‌های خروجی</td>
                        <td style={{ textAlign: "left" }} id="cash-flow-total-outflow">
                          {formatMoney(cf.totalOutflowRial)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Cash Flow Summary & Reconciliation */}
                <div
                  className="row wrap justify-between items-center"
                  style={{
                    padding: "var(--s4)",
                    background: "var(--bg-surface)",
                    borderRadius: "var(--r-card)",
                    border: "1px solid var(--line)",
                  }}
                >
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <div className="text-sm font-semibold">خالص تغییرات وجه نقد در دوره:</div>
                    <div
                      className="text-lg font-bold"
                      id="cash-flow-net"
                      style={{
                        color: cf.netCashFlowRial >= 0 ? "var(--c-success)" : "var(--c-danger)",
                      }}
                    >
                      {formatMoney(cf.netCashFlowRial)} ریال
                    </div>
                  </div>

                  <div className="row items-center" style={{ gap: "var(--s2)" }}>
                    <span id="cash-flow-reconciled">
                      <Chip tone={cf.reconciliationDifferenceRial === 0 ? "ok" : "coral"}>
                        {cf.reconciliationDifferenceRial === 0
                          ? "تطبیق دفاتر ۱۰۰٪ (بدون مغایرت)"
                          : `مغایرت دفاتر: ${formatMoney(Math.abs(cf.reconciliationDifferenceRial))} ریال`}
                      </Chip>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
